import React, { useMemo } from 'react';

import { useThemeObserver } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data';

import OriginTracing from '@atlassiansox/origin-tracing';

import { useLockScreenState } from './LockScreenStore';
import { LockScreenContent, LockScreenModalContent } from './LockScreenContent';
import { NonDismissibleWrapper } from './NonDismissibleWrapper';
import { LockScreenModal } from './LockScreenModal';
import { AnalyticsProvider } from './Analytics';

export const LockScreen = () => {
	const { colorMode } = useThemeObserver();
	const { edition } = useSessionData();
	const { isLocked, entitlementDetails, lockScreenDetails } = useLockScreenState();

	const { originTracing, analyticsAttributes } = useMemo(
		() => ({
			analyticsAttributes: {
				lockScreenDetails,
				edition,
				entitlementDetails,
			},
			// generate a new originId when we update analytics attributes
			originTracing: new OriginTracing({ product: 'confluence' }),
		}),
		[edition, entitlementDetails, lockScreenDetails],
	);

	const isDarkMode = colorMode === 'dark';

	const lockScreenProps = useMemo(
		() => ({
			...lockScreenDetails,
			originTracing,
			isDarkMode,
			entitlementId: entitlementDetails?.entitlementId,
		}),
		[entitlementDetails?.entitlementId, isDarkMode, lockScreenDetails, originTracing],
	);

	if (!isLocked || !lockScreenDetails) return null;

	const isDismissible = !lockScreenDetails.usageOverLimit && !lockScreenDetails.isBillingAdmin;

	if (isDismissible) {
		return (
			<AnalyticsProvider attributes={analyticsAttributes}>
				<LockScreenModal>
					<LockScreenModalContent {...lockScreenProps} />
				</LockScreenModal>
			</AnalyticsProvider>
		);
	}

	return (
		<AnalyticsProvider attributes={analyticsAttributes}>
			<NonDismissibleWrapper originTracing={originTracing} isDarkMode={isDarkMode}>
				<LockScreenContent {...lockScreenProps} />
			</NonDismissibleWrapper>
		</AnalyticsProvider>
	);
};
